/**
 * Parses a date-only ISO string (YYYY-MM-DD) as UTC midnight.
 * Using `new Date("YYYY-MM-DD")` is ambiguous across browsers — some parse it
 * as local time, others as UTC — which causes off-by-one-day errors for users
 * in UTC+ timezones. This function always yields UTC midnight.
 */
export const parseDateUTC = (isoDateString) => {
  const [ year, month, day ] = isoDateString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day))
}

/** Returns the number of days in a given UTC year/month (month is 0-indexed). */
const getDaysInMonth = (year, month) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate()

/**
 * Translates a time series to a new starting point
 * @param isoTimestampStrings
 * @param newStartingDate
 * @returns The newly translated Date objects
 */
export const translateTimeSeries = (isoTimestampStrings, newStartingDate) => {
  const sortedDates = isoTimestampStrings
    .map(iso => parseDateUTC(iso))
    .sort((a, b) => a - b)

  const baseTime = sortedDates[0].getTime()
  const referenceTime = newStartingDate.getTime()

  return sortedDates.map(d => new Date(referenceTime + (d - baseTime)))
}

/**
 * Given a past date, finds the equivalent date in the first available year
 * starting from `today`, preserving the month, day-of-week, and
 * nth-occurrence-within-month.
 *
 * Example: 3rd Friday of June 2022 → 3rd Friday of June 2026 (if today is March 2026)
 *
 * If the nth occurrence doesn't exist in the target year (e.g. 6th Friday
 * of September when only 5 exist), it falls back to the last occurrence
 * of that weekday in that month.
 */
export const calculateFutureEquivalentDate = (pastDate, today) => {
  const dayOfWeek = pastDate.getUTCDay()
  const month = pastDate.getUTCMonth()

  // Calculate which nth occurrence of this weekday the pastDate is
  // (e.g. 1st Monday, 3rd Friday, etc.)
  const nthOccurrence = Math.ceil(pastDate.getUTCDate() / 7)

  // Find the first year where this month hasn't passed yet
  let targetYear = today.getUTCFullYear()
  const todayMonth = today.getUTCMonth()
  const todayDate = today.getUTCDate()

  let targetDay = getNthWeekdayInMonth(targetYear, month, dayOfWeek, nthOccurrence)
  if (month < todayMonth || (month === todayMonth && targetDay <= todayDate)) {
    targetYear++
    targetDay = getNthWeekdayInMonth(targetYear, month, dayOfWeek, nthOccurrence)
  }

  if (targetDay !== null) {
    return new Date(Date.UTC(targetYear, month, targetDay))
  }

  // Overflow: pick the last occurrence of this weekday in the month
  const lastDay = getLastWeekdayInMonth(targetYear, month, dayOfWeek)
  return new Date(Date.UTC(targetYear, month, lastDay))
}

/**
 * Returns the day-of-month for the nth occurrence of a given weekday
 * in a specific year/month, or null if it doesn't exist.
 *
 * @param {number} year
 * @param {number} month - 0-indexed
 * @param {number} dayOfWeek - 0=Sun, 6=Sat
 * @param {number} n - 1-based occurrence
 * @returns {number|null}
 */
const getNthWeekdayInMonth = (year, month, dayOfWeek, n) => {
  // Find the first occurrence of this weekday in the month
  const firstOfMonth = new Date(Date.UTC(year, month, 1))
  const firstOccurrenceDay = 1 + ((dayOfWeek - firstOfMonth.getUTCDay() + 7) % 7)

  const targetDay = firstOccurrenceDay + (n - 1) * 7
  return targetDay <= getDaysInMonth(year, month) ? targetDay : null
}

/**
 * Returns the day-of-month for the last occurrence of a given weekday
 * in a specific year/month.
 */
const getLastWeekdayInMonth = (year, month, dayOfWeek) => {
  const daysInMonth = getDaysInMonth(year, month)
  const diff = (new Date(Date.UTC(year, month, daysInMonth)).getUTCDay() - dayOfWeek + 7) % 7
  return daysInMonth - diff
}

/** Returns today at UTC midnight */
export const todayUTC = () => {
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/** Formats a Date to YYYY-MM-DD using UTC components */
export const toISODateString = (date) => date.toISOString().split('T')[0]
